import React, { useState, useMemo } from 'react'
import {
  ChevronLeft, ChevronRight, ChevronDown, ArrowDown, ArrowUp, Download, Search,
  SlidersHorizontal, Eye, Filter, X, FileDown, MoreHorizontal, FileSpreadsheet, Loader2, FileType
} from 'lucide-react'
import { formatCurrency, formatDate, formatCompactNumber, formatNumber, formatDATable, formatQtyTable, formatPriceTable, truncateText } from '../utils/helpers'
import { toast } from 'react-hot-toast'
import useStore from '../store/useStore'
import { cachedApiService } from '../services/api'
import ErrorMessage from './ErrorMessage'
import Skeleton from 'react-loading-skeleton'

const TableSkeleton = () => (
  <div className="card">
    <div className="mb-3">
      <div className="skeleton h-5 w-40 rounded"></div>
    </div>
    <div className="space-y-2">
      {[...Array(6)].map((_, i) => (
        <div key={i} className="skeleton h-12 w-full rounded-lg"></div>
      ))}
    </div>
  </div>
)

const DataTableEnhanced = ({ data, loading = false, onPageChange, onSortChange }) => {
  // ⚠️ TOUS LES HOOKS DOIVENT ÊTRE DÉCLARÉS AVANT LES RETURNS CONDITIONNELS
  const [sortField, setSortField] = useState(null)
  const [sortDirection, setSortDirection] = useState('asc')
  const [columnFilters, setColumnFilters] = useState({})
  const [showFilters, setShowFilters] = useState(false)
  const [density, setDensity] = useState('normal') // compact, normal, comfortable
  const [visibleColumns, setVisibleColumns] = useState({
    date_declaration: true,
    numero_declaration: true,
    designation_commercial: true,
    nom_raison_sociale: true,
    fournisseur: true,
    pays_origine: true,
    quantite: true,
    prix_unitaire: true,
    monnaie_ptfn: false,
    prix_unitaire_da: true,
    poids_net_article: true,
    montant_valeur_article_da: true,
  })
  const [showColumnSettings, setShowColumnSettings] = useState(false)
  const [showExportMenu, setShowExportMenu] = useState(false)
  const [exporting, setExporting] = useState(false)
  const { filters } = useStore()

  // Filter data based on column filters (client-side for quick feedback)
  const filteredData = useMemo(() => {
    if (!data?.data) return []
    if (Object.keys(columnFilters).length === 0) return data.data

    return data.data.filter(row => {
      return Object.entries(columnFilters).every(([column, filterValue]) => {
        if (!filterValue) return true
        const cellValue = String(row[column] || '').toLowerCase()
        return cellValue.includes(filterValue.toLowerCase())
      })
    })
  }, [data?.data, columnFilters])

  // ✅ MAINTENANT ON PEUT FAIRE DES RETURNS CONDITIONNELS
  if (loading) {
    return <TableSkeleton />
  }

  if (!data) {
    return (
      <div className="card">
        <h3 className="section-title mb-4">Données détaillées</h3>
        <ErrorMessage 
          error="Aucune donnée disponible"
          title="Données non chargées"
          showRetry={false}
        />
      </div>
    )
  }

  if (!data.data || data.data.length === 0) {
    return (
      <div className="card">
        <h3 className="section-title mb-4">Données détaillées</h3>
        <div className="text-center py-8">
          <FileDown className="mx-auto h-10 w-10 text-gray-300 dark:text-gray-600" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">Aucune donnée trouvée</h3>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Aucun résultat ne correspond aux filtres sélectionnés.
          </p>
        </div>
      </div>
    )
  }

  const handleSort = (field) => {
    const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc'
    setSortField(field)
    setSortDirection(newDirection)
    if (onSortChange) {
      onSortChange(field, newDirection)
    }
  }

  const handleColumnFilterChange = (column, value) => {
    setColumnFilters(prev => ({
      ...prev,
      [column]: value
    }))
  }

  const clearColumnFilter = (column) => {
    setColumnFilters(prev => {
      const newFilters = { ...prev }
      delete newFilters[column]
      return newFilters
    })
  }

  const clearAllFilters = () => {
    setColumnFilters({})
  }

  const handleExport = async (format) => {
    setShowExportMenu(false)
    if (exporting) return
    setExporting(true)
    const toastId = toast.loading(
      `Export ${format === 'excel' ? 'Excel' : 'CSV'} en cours… (${data.total.toLocaleString()} lignes)`,
      { duration: Infinity }
    )
    try {
      await cachedApiService.exportData(filters, format, sortField, sortDirection)
      toast.success(
        `Export terminé : ${data.total.toLocaleString()} lignes exportées`,
        { id: toastId, duration: 4000 }
      )
    } catch (err) {
      console.error('Export error:', err)
      const msg = err?.response?.status === 404
        ? 'Aucune donnée à exporter pour ces filtres'
        : `Erreur export: ${err.message || 'échec'}`
      toast.error(msg, { id: toastId, duration: 5000 })
    } finally {
      setExporting(false)
    }
  }

  const SortIcon = ({ field }) => {
    if (sortField !== field) {
      return <ArrowUp className="w-3.5 h-3.5 text-gray-300 dark:text-gray-600" />
    }
    return sortDirection === 'asc' ? 
      <ArrowUp className="w-3.5 h-3.5 text-primary-600 dark:text-primary-400" /> : 
      <ArrowDown className="w-3.5 h-3.5 text-primary-600 dark:text-primary-400" />
  }

  const columns = [
    { key: 'date_declaration', label: 'Date', sortable: true, width: 'w-32' },
    { key: 'numero_declaration', label: 'N° Déclaration', sortable: true, width: 'w-40' },
    { key: 'designation_commercial', label: 'Produit', sortable: true, width: 'w-64' },
    { key: 'nom_raison_sociale', label: 'Importateur', sortable: true, width: 'w-48' },
    { key: 'fournisseur', label: 'Fournisseur', sortable: true, width: 'w-48' },
    { key: 'pays_origine', label: 'Pays', sortable: true, width: 'w-32' },
    { key: 'quantite', label: 'Quantité', sortable: true, width: 'w-32' },
    { key: 'prix_unitaire', label: 'Prix Unit.', sortable: true, width: 'w-32' },
    { key: 'monnaie_ptfn', label: 'Devise', sortable: true, width: 'w-24' },
    { key: 'prix_unitaire_da', label: 'Prix Unit. (DA)', sortable: true, width: 'w-36' },
    { key: 'poids_net_article', label: 'Poids (kg)', sortable: true, width: 'w-32' },
    { key: 'montant_valeur_article_da', label: 'Valeur (DA)', sortable: true, width: 'w-40' },
  ]

  const visibleColumnsArray = columns.filter(col => visibleColumns[col.key])
  const activeFiltersCount = Object.keys(columnFilters).length

  const totalPages = Math.ceil(data.total / data.size)
  const startItem = (data.page - 1) * data.size + 1
  const endItem = Math.min(data.page * data.size, data.total)

  const densityClasses = {
    compact: 'px-3 py-2',
    normal: 'px-4 py-3',
    comfortable: 'px-6 py-5'
  }

  // Filter row padding matches the density setting (was hardcoded px-2 py-1.5)
  const filterRowClass = {
    compact: 'px-3 py-1.5',
    normal: 'px-4 py-2',
    comfortable: 'px-6 py-2.5'
  }[density]

  const toggleColumnVisibility = (columnKey) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }))
  }

  return (
    <div className="card">
      {/* Header avec contrôles */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h3 className="section-title text-sm">Données détaillées</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 tabular-nums">
            {filteredData.length !== data.total 
              ? `${filteredData.length} / ${data.total.toLocaleString()} résultats`
              : `${data.total.toLocaleString()} résultats`
            }
          </p>
        </div>
        
        <div className="flex items-center gap-1.5">
          {/* Filtres par colonne */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all ${
              showFilters || activeFiltersCount > 0
                ? 'bg-primary-600 text-white hover:bg-primary-700' 
                : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:border-primary-300'
            }`}
            title="Filtres par colonne"
          >
            <Filter className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Filtres</span>
            {activeFiltersCount > 0 && (
              <span className="bg-white/20 text-white rounded-full px-1.5 py-0.5 text-[10px] font-bold">
                {activeFiltersCount}
              </span>
            )}
          </button>

          {/* More options dropdown (density + columns) */}
          <div className="relative group">
            <button
              className="inline-flex items-center p-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-500 dark:text-gray-400 hover:border-primary-300 transition-colors"
              title="Options"
            >
              <SlidersHorizontal className="w-3.5 h-3.5" />
            </button>
            <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
              <div className="p-1.5 border-b border-gray-100 dark:border-gray-800">
                <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider px-2 py-1">Densité</p>
                {['compact', 'normal', 'comfortable'].map(d => (
                  <button
                    key={d}
                    onClick={() => setDensity(d)}
                    className={`w-full text-left px-2 py-1.5 text-xs rounded-md ${
                      density === d 
                        ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium' 
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    {d === 'compact' ? 'Compact' : d === 'normal' ? 'Normal' : 'Confortable'}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Colonnes visibles */}
          <div className="relative">
            <button
              onClick={() => setShowColumnSettings(!showColumnSettings)}
              className="inline-flex items-center p-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-500 dark:text-gray-400 hover:border-primary-300 transition-colors"
              title="Colonnes visibles"
            >
              <Eye className="w-3.5 h-3.5" />
            </button>
            {showColumnSettings && (
              <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-20 max-h-80 overflow-y-auto">
                <div className="p-2.5 border-b border-gray-100 dark:border-gray-800">
                  <p className="text-xs font-semibold text-gray-900 dark:text-gray-100">Colonnes</p>
                </div>
                <div className="p-1">
                  {columns.map(col => (
                    <label
                      key={col.key}
                      className="flex items-center px-2.5 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer rounded-md"
                    >
                      <input
                        type="checkbox"
                        checked={visibleColumns[col.key]}
                        onChange={() => toggleColumnVisibility(col.key)}
                        className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500 mr-2.5 w-3.5 h-3.5"
                      />
                      <span className="text-xs text-gray-700 dark:text-gray-300">{col.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Export — dropdown with CSV / Excel options */}
          <div className="relative">
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              disabled={exporting}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs font-medium text-gray-600 dark:text-gray-300 hover:border-emerald-400 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Exporter les données"
            >
              {exporting ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Download className="w-3.5 h-3.5" />
              )}
              <span className="hidden sm:inline">{exporting ? 'Export…' : 'Export'}</span>
              {!exporting && <ChevronDown className="w-3 h-3" />}
            </button>
            {showExportMenu && !exporting && (
              <>
                {/* Click-outside overlay */}
                <div className="fixed inset-0 z-10" onClick={() => setShowExportMenu(false)} />
                <div className="absolute right-0 mt-2 w-52 bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-20 overflow-hidden">
                  <div className="p-1.5 border-b border-gray-100 dark:border-gray-800">
                    <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider px-2 py-1">
                      Exporter {data.total.toLocaleString()} lignes
                    </p>
                  </div>
                  <div className="p-1">
                    <button
                      onClick={() => handleExport('excel')}
                      className="w-full flex items-center gap-2.5 px-2.5 py-2 text-xs rounded-md text-gray-700 dark:text-gray-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors"
                    >
                      <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
                      <div className="text-left">
                        <div className="font-medium">Excel (.xlsx)</div>
                        <div className="text-[10px] text-gray-400">Formaté, largeurs auto</div>
                      </div>
                    </button>
                    <button
                      onClick={() => handleExport('pdf')}
                      className="w-full flex items-center gap-2.5 px-2.5 py-2 text-xs rounded-md text-gray-700 dark:text-gray-300 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    >
                      <FileType className="w-4 h-4 text-red-600" />
                      <div className="text-left">
                        <div className="font-medium">PDF (Rapport)</div>
                        <div className="text-[10px] text-gray-400">KPI + graphiques + données</div>
                      </div>
                    </button>
                    <button
                      onClick={() => handleExport('csv')}
                      className="w-full flex items-center gap-2.5 px-2.5 py-2 text-xs rounded-md text-gray-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                    >
                      <FileDown className="w-4 h-4 text-blue-600" />
                      <div className="text-left">
                        <div className="font-medium">CSV (;)</div>
                        <div className="text-[10px] text-gray-400">UTF-8, séparateur point-virgule</div>
                      </div>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Ligne de filtres actifs */}
      {activeFiltersCount > 0 && (
        <div className="mb-3 flex items-center gap-1.5 flex-wrap">
          <span className="text-[10px] text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wider">Filtres:</span>
          {Object.entries(columnFilters).map(([column, value]) => (
            <span
              key={column}
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-md text-[11px]"
            >
              <span className="font-medium">{columns.find(c => c.key === column)?.label}:</span>
              <span>{value}</span>
              <button
                onClick={() => clearColumnFilter(column)}
                className="ml-0.5 text-primary-500 hover:text-primary-700 dark:hover:text-primary-200"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
          <button
            onClick={clearAllFilters}
            className="text-[11px] text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400 underline"
          >
            Effacer
          </button>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto border border-gray-200 dark:border-gray-800 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
          <thead className="bg-gray-50 dark:bg-gray-800/50">
            {/* En-têtes de colonnes */}
            <tr>
              {visibleColumnsArray.map((column) => (
                <th
                  key={column.key}
                  className={`${densityClasses[density]} text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider ${
                    column.sortable ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none' : ''
                  } transition-colors`}
                  onClick={column.sortable ? () => handleSort(column.key) : undefined}
                >
                  <div className="flex items-center space-x-1">
                    <span>{column.label}</span>
                    {column.sortable && <SortIcon field={column.key} />}
                  </div>
                </th>
              ))}
            </tr>
            
            {/* Ligne de filtres */}
            {showFilters && (
              <tr className="bg-primary-50/50 dark:bg-primary-950/20">
                {visibleColumnsArray.map((column) => (
                  <th key={`filter-${column.key}`} className={filterRowClass}>
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3 h-3 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Filtrer..."
                        value={columnFilters[column.key] || ''}
                        onChange={(e) => handleColumnFilterChange(column.key, e.target.value)}
                        className="w-full pl-7 pr-2 py-1 text-xs border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </th>
                ))}
              </tr>
            )}
          </thead>
          
          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-100 dark:divide-gray-800">
            {filteredData.map((row, index) => {
              const uniqueKey = `${row.id}-${row.numero_declaration}-${row.numero_article}-${index}`
              return (
                <tr key={uniqueKey} className="table-row-enter hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors" style={{ animationDelay: `${Math.min(index * 15, 300)}ms` }}>
                  {visibleColumns.date_declaration && (
                    <td className={`${densityClasses[density]} whitespace-nowrap text-sm text-gray-900 dark:text-gray-200`}>
                      {formatDate(row.date_declaration)}
                    </td>
                  )}
                  {visibleColumns.numero_declaration && (
                    <td className={`${densityClasses[density]} text-sm text-gray-900 dark:text-gray-200 tabular-nums`}>
                      <span className="font-mono text-xs">
                        {truncateText(row.numero_declaration || 'N/A', 15)}
                      </span>
                    </td>
                  )}
                  {visibleColumns.designation_commercial && (
                    <td className={`${densityClasses[density]} text-sm text-gray-900 dark:text-gray-200`}>
                      <span title={row.designation_commercial} className="font-medium">
                        {truncateText(row.designation_commercial || 'N/A', 40)}
                      </span>
                    </td>
                  )}
                  {visibleColumns.nom_raison_sociale && (
                    <td className={`${densityClasses[density]} text-sm text-gray-900 dark:text-gray-200`}>
                      <div>
                        <span title={row.nom_raison_sociale}>
                          {truncateText(row.nom_raison_sociale || 'N/A', 25)}
                        </span>
                        {row.nif_importateur && (
                          <span className="text-xs text-gray-400 block mt-1 font-mono">
                            {truncateText(row.nif_importateur, 12)}
                          </span>
                        )}
                      </div>
                    </td>
                  )}
                  {visibleColumns.fournisseur && (
                    <td className={`${densityClasses[density]} text-sm text-gray-900 dark:text-gray-200`}>
                      <span title={row.fournisseur}>
                        {truncateText(row.fournisseur || 'N/A', 25)}
                      </span>
                    </td>
                  )}
                  {visibleColumns.pays_origine && (
                    <td className={`${densityClasses[density]} whitespace-nowrap text-sm`}>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300">
                        {row.pays_origine || 'N/A'}
                      </span>
                    </td>
                  )}
                  {visibleColumns.quantite && (
                    <td className={`${densityClasses[density]} whitespace-nowrap text-sm text-gray-900 dark:text-gray-200`}>
                      <div className="text-right tabular-nums">
                        {(() => { const q = formatQtyTable(row.quantite || 0); return (
                          <span className="font-medium" title={q.title}>{q.display}</span>
                        ) })()}
                        <span className="text-xs text-gray-500 dark:text-gray-400 block">{row.unite_facturation || 'unité'}</span>
                      </div>
                    </td>
                  )}
                  {visibleColumns.prix_unitaire && (
                    <td className={`${densityClasses[density]} whitespace-nowrap text-sm text-gray-900 dark:text-gray-200`}>
                      <div className="text-right tabular-nums">
                        {(() => { const p = formatPriceTable(row.prix_unitaire || 0); return (
                          <span className="font-medium text-blue-600 dark:text-blue-400" title={p.title}>{p.display}</span>
                        ) })()}
                        <span className="text-xs text-gray-500 dark:text-gray-400 block">{row.monnaie_ptfn || 'DA'}</span>
                      </div>
                    </td>
                  )}
                  {visibleColumns.monnaie_ptfn && (
                    <td className={`${densityClasses[density]} whitespace-nowrap text-sm`}>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300">
                        {row.monnaie_ptfn || 'DA'}
                      </span>
                    </td>
                  )}
                  {visibleColumns.prix_unitaire_da && (
                    <td className={`${densityClasses[density]} whitespace-nowrap text-sm text-gray-900 dark:text-gray-200`}>
                      <div className="text-right tabular-nums">
                        {(() => { const p = formatPriceTable((row.montant_valeur_article_da || 0) / (row.quantite || 1)); return (
                          <span className="font-medium text-purple-600 dark:text-purple-400" title={p.title}>{p.display}</span>
                        ) })()}
                        <span className="text-xs text-gray-500 dark:text-gray-400 block">DA</span>
                      </div>
                    </td>
                  )}
                  {visibleColumns.poids_net_article && (
                    <td className={`${densityClasses[density]} whitespace-nowrap text-sm text-gray-900 dark:text-gray-200`}>
                      <div className="text-right tabular-nums">
                        {(() => { const w = formatQtyTable(row.poids_net_article || 0); return (
                          <span className="font-medium" title={w.title + ' kg'}>{w.display}</span>
                        ) })()}
                        <span className="text-xs text-gray-500 dark:text-gray-400 block">kg</span>
                      </div>
                    </td>
                  )}
                  {visibleColumns.montant_valeur_article_da && (
                    <td className={`${densityClasses[density]} whitespace-nowrap text-sm text-gray-900 dark:text-gray-200`}>
                      <div className="text-right tabular-nums">
                        {(() => { const v = formatDATable(row.montant_valeur_article_da || 0); return (
                          <span className="font-semibold text-green-600 dark:text-green-400" title={v.title}>{v.display}</span>
                        ) })()}
                        <span className="text-xs text-gray-500 dark:text-gray-400 block">DA</span>
                      </div>
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row items-center justify-between mt-4 pt-4 border-t border-gray-100 dark:border-gray-800 gap-3">
        <div className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
          <span className="font-medium text-gray-700 dark:text-gray-300">{startItem}</span>–<span className="font-medium text-gray-700 dark:text-gray-300">{endItem}</span> sur{' '}
          <span className="font-medium text-gray-700 dark:text-gray-300">{data.total.toLocaleString()}</span>
        </div>
        
        <div className="flex items-center gap-1">
          <button
            onClick={() => onPageChange && onPageChange(data.page - 1)}
            disabled={data.page === 1}
            className="p-1.5 rounded-md border border-gray-200 dark:border-gray-700 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <ChevronLeft className="w-4 h-4 text-gray-600 dark:text-gray-400" />
          </button>
          
          {/* Mobile: simple page indicator */}
          <span className="sm:hidden text-xs font-medium text-gray-600 dark:text-gray-400 px-2 tabular-nums">
            {data.page} / {totalPages}
          </span>
          
          {/* Desktop: page numbers */}
          <div className="hidden sm:flex gap-0.5">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNumber
              if (totalPages <= 5) {
                pageNumber = i + 1
              } else if (data.page <= 3) {
                pageNumber = i + 1
              } else if (data.page >= totalPages - 2) {
                pageNumber = totalPages - 4 + i
              } else {
                pageNumber = data.page - 2 + i
              }
              
              return (
                <button
                  key={pageNumber}
                  onClick={() => onPageChange && onPageChange(pageNumber)}
                  className={`min-w-[32px] h-8 rounded-md text-xs font-medium transition-colors ${
                    pageNumber === data.page
                      ? 'bg-primary-600 text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                >
                  {pageNumber}
                </button>
              )
            })}
          </div>
          
          <button
            onClick={() => onPageChange && onPageChange(data.page + 1)}
            disabled={data.page >= totalPages}
            className="p-1.5 rounded-md border border-gray-200 dark:border-gray-700 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <ChevronRight className="w-4 h-4 text-gray-600 dark:text-gray-400" />
          </button>
        </div>
      </div>
    </div>
  )
}

export default DataTableEnhanced
